<template>
  <div class="flex flex-col h-full w-full">
    <LoadingView v-if="isLoading" />
    <header class="flex items-center justify-between gap-4 flex-shrink-0 w-full">
      <h1 class="flex-0 text-3xl leading-9 font-headline font-medium antialiased  bg-gradient-to-r from-bhav-cool-gray-900 to-bhav-cool-gray-600 text-transparent bg-clip-text inline-block">
        Users
      </h1>
      <FormButton :to="{name: 'user-add', }" prefix-class="fak" icon="fa-plus" color="blue">Add New User</FormButton>
    </header>
    <div class="flex flex-col mt-4 p-4 rounded-lg">
      <div class="inline-flex flex-col flex-wrap md:flex-row md:justify-start gap-6">
        <div class="mb-4 flex-initial">
          <label for="search" class="block text-sm font-medium leading-5 pb-1.5 text-blue-500">Search by Keyword</label>
          <SearchInput v-model="query.search" color="gray" placeholder="Search..." />
        </div>
        <div class="mb-4 flex-initial">
            <SelectField v-model="query.status" label="Status" empty="All Statuses" :options="[{ label: 'Active', value: 'active'}, { label: 'Inactive', value: 'inactive'} ]" />
        </div>
        <div class="mb-4 flex-initial">
          <SelectMultipleField v-model="query.levels" label="Access Level" empty="All Access Levels" all="All Access Levels" :options="UserLevels.levels" />
        </div>
        <div v-if="hasFilters" class="mb-4 flex-initial sm:mt-5">
          <FormButton @click="reset" color="blue">Clear Filters</FormButton>
        </div>
      </div>
      <PaginationTable
        empty-message="There are currently no user items."
        v-if="!isLoading"
        :legacy="false"
        :columns="columns"
        view="users-list"
        :data="filtered">
        <template #col-name="{ row }">
          <router-link class="block py-4 whitespace-nowrap" :to="{ name: 'user-edit', params: { id: row.id } }">
            <div class="flex items-center">
              <div class="flex-shrink-0 rounded-full overflow-hidden flex items-center justify-center bg-bhav-blue-400 text-white h-10 w-10">
                <img v-if="row.image" class="aspect-square object-cover"
                  :src="row.image" :alt="row.name">
                <i v-else class="fas fa-user"></i>
              </div>
              <div class="ml-4">
                <div class="text-sm leading-5 font-medium text-bhav-blue-500">
                  {{ row.name }}
                </div>
              </div>
            </div>
          </router-link>
        </template>

        <template #col-email="{ row }">
          <router-link class="block py-4 whitespace-nowrap" :to="{ name: 'user-edit', params: { id: row.id } }">
            <p class="text-sm leading-5 text-bhav-blue-500">
              {{ row.email }}
            </p>
          </router-link>
        </template>

        <template #col-last_login="{ row }">
          <router-link class="block py-4 whitespace-nowrap" :to="{ name: 'user-edit', params: { id: row.id } }">
            <p class="text-sm leading-5 text-bhav-blue-500">
              {{ getDate(row) }}
            </p>
          </router-link>
        </template>

        <template #col-level="{ row }">
          <router-link class="block py-4 whitespace-nowrap" :to="{ name: 'user-edit', params: { id: row.id } }">
            <span class="px-3 h-8 items-center justify-center inline-flex text-xs leading-5 font-semibold rounded-3xl bg-bhav-green-100 text-bhav-green-600">
              {{ UserLevels.label(row.level) }}
            </span>
          </router-link>
        </template>

        <template #col-status="{ row }">
          <div class="flex items-center gap-2">
            <ToggleButton
              :id="`item-${row.id}-status`"
              @changed="(e) => save(e, row)"
              :checked="row.deleted_at === null"
              on-color="green"
              :label="row.deleted_at === null ? 'Active' : 'Inactive'"/>
          </div>
        </template>
      </PaginationTable>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import LoadingView from '@/components/LoadingView.vue';
import {ToggleButton,FormButton,SelectField, SelectMultipleField} from '@/components/form';
import PaginationTable from "@/components/PaginationTable.vue";
import UserLevels from "@/enums/UserLevels";
import User from "@/models/user";
import SearchInput from "@/components/form/SearchInput.vue";

const query = {
    search: '',
    status: 'active',
    levels: []
}

export default {
  setup() {
        return {
            UserLevels
        }
    },
    data() {
        return {
            isLoading: true,
            query: {...query},
            users: [],
            columns: [
                {
                    name: 'name',
                    label: 'Name',
                    default: true,
                    sort: (item) => item.name || ''
                },
                {
                    name: 'email',
                    label: 'Email',
                    default: true,
                    sort: (item) => item.email || ''
                },
                {
                    name: 'last_login',
                    label: 'Last Login',
                    default: true,
                    sort: (item) => item.last_login || item.updated_at || ''
                },
                {
                    name: 'level',
                    label: 'Level',
                    default: true,
                    sort: (item) => item.level ?? 0
                },
                {
                    name: 'status',
                    label: 'Status',
                    default: true,
                    sort: (item) => item.deleted_at === null ? 'active' : 'inactive'
                }
            ]
        }
    },
    components: {
        LoadingView,
        FormButton,
        ToggleButton,
        SelectField,
        SelectMultipleField,
        PaginationTable,
        SearchInput
    },
    methods: {
        index() {
            axios.get('users')
                .then(resp => {
                    this.users = resp.data.map(user => new User(user));
                    this.isLoading = false;
                })
                .catch(error => {
                    console.error(error);
                })
        },
        getDate(item) {
          return new Intl.DateTimeFormat('en-us', {
                      month:'short',
                      day:'numeric',
                      year:'numeric',
                      hour:'numeric',
                      hour12:true,
                      minute:'2-digit',
                      second:'2-digit',
                      timeZone:'America/Denver',
                      timeZoneName:'short'
                      }).format(new Date(item.last_login || item.updated_at))
        },
        save(e, item) {
          this.isLoading = true;
          if(e) { // The item should be restored
            axios.patch(`users/${item.id}/restore`)
              .then(() => {
                this.index();
              })
          } else { // The item should be destroyed
            axios.delete(`users/${item.id}`)
              .then(() => {
                this.index();
              })
          }
        },
        reset() {
            this.query = {...query};
        }
    },
    computed: {
        hasFilters() {
            return JSON.stringify(this.query) != JSON.stringify(query);
        },
        filtered() {
            let results = this.users;

            if (this.query.status !== null) {
                results = results.filter(item => {
                    if(this.query.status == 'active') 
                        return item.deleted_at === null;
                    return item.deleted_at !== null;
                })
            }

            if (this.query.levels.length > 0) {
                results = results.filter(item => this.query.levels.includes(item.level));
            }

            if (this.query.search != '') {
                const searchTerm = this.query.search.toLowerCase();
                results = results.filter(item => {
                    const name = (item.name || '').toLowerCase();
                    const email = (item.email || '').toLowerCase();
                    const last_login_at = (this.getDate(item) || '').toLowerCase();

                    return name.includes(searchTerm) || email.includes(searchTerm) || last_login_at.includes(searchTerm);
                });
            } 
            return results;
        },
    },
    mounted() {
      this.index();
    }
}
</script>@/components/form